import { IOrganization, IQuoteLineItem, QuoteCreateRequest, QuoteSegment } from "../../shared/types";
import { calculateQuoteLinePricing } from "../../components/QuoteLineItemGrid/quoteLineItemUtils";
import { EditorSection, toSectionRequest } from "../../components/QuoteSectionBar/sectionUtils";

export const quoteSegments: QuoteSegment[] = ["Commercial", "Charity", "Education", "Government"];

export const defaultQuoteName = (targetName: string, now = new Date()) => targetName.trim()
  ? `${targetName.trim()} - ${now.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
  : "";

export const newQuoteLine = (segment: QuoteSegment = "Commercial"): IQuoteLineItem => ({
  product_id: "", sku_id: "", sku_title: "", segment, term_duration: "", quantity: 1,
  renewal_date: null,
  msrp_unit_price: null, sale_unit_price: null, total_price: null, is_price_manual: false, is_price_unavailable: false,
  price_unavailable_reason: null, note: null,
  inherits_section_dates: true,
});

export const lineIsComplete = (line: IQuoteLineItem) => Boolean(
  line.product_id && line.sku_id && line.sku_title && line.segment && line.term_duration && line.quantity > 0
  && !line.is_price_unavailable
  // Issue 50: a manual line is complete once its raw manual_msrp_unit_price/manual_cost_unit_price
  // inputs are present - msrp_unit_price/cost_unit_price/sale_unit_price are now purely the
  // server's computed output and stay null until a preview/save response arrives. Gating on them
  // here (as before) would make canPreview/canSave never become true in the first place.
  && (!line.is_price_manual || (line.manual_msrp_unit_price != null && line.manual_cost_unit_price != null
    && line.manual_msrp_unit_price >= 0 && line.manual_cost_unit_price >= 0 && line.manual_cost_unit_price <= line.manual_msrp_unit_price
    && line.requested_discount_pct != null && line.requested_discount_pct >= 0 && line.requested_discount_pct <= 100))
);

export const appendImportedQuoteLines = (current: IQuoteLineItem[], imported: IQuoteLineItem[]) =>
  [...current, ...imported.map(line => {
    const quantity = Math.max(1, Number(line.quantity) || 1);
    if (line.sale_unit_price == null) return { ...line, quantity, total_price: null, margin: null };
    const priced = calculateQuoteLinePricing(line.sale_unit_price, line.cost_unit_price, quantity);
    return { ...line, quantity, sale_unit_price: priced.sale, total_price: priced.total, margin: priced.margin };
  })];

export const findActiveOrganizationByCustomerGuid = (
  organizations: IOrganization[],
  customerGuid: string | null
) => customerGuid
  ? organizations.find(organization => organization.is_active
    && organization.customer_guid.toLowerCase() === customerGuid.toLowerCase()) || null
  : null;

// Re-resolve an already-selected customer against the newest fetched list, so the selection stays
// put while the customer's own fields (the three discount percentages above all) track the server.
// Without this, whichever snapshot the picker first latched onto is the one every downstream
// discount read keeps using - the bug behind "changed an organization's discounts, clicked Create
// Quote, still saw the old percentages", which invalidating caches alone could never fix because
// the refetched values did arrive and were simply ignored. Falls back to the stored snapshot when
// the customer isn't in the list (a refetch that dropped or deactivated them mid-edit) so the
// selection never silently empties itself.
export const resolveSelectedCustomer = (
  organizations: IOrganization[],
  selected: IOrganization | null
) => selected
  ? organizations.find(organization => organization.customer_guid === selected.customer_guid) ?? selected
  : null;

type FormValues = {
  quoteType: "customer" | "prospect";
  customerId: string;
  prospectName: string;
  discounts: { monthly: string; installment: string; annual: string };
  name: string;
  description: string;
  hasAffiliate?: boolean;
  affiliateName?: string;
  sections: EditorSection[];
};

export const buildCreateQuoteRequest = (values: FormValues): QuoteCreateRequest => {
  const prospect = values.quoteType === "prospect";
  return {
    quote_type: values.quoteType,
    customer_id: prospect ? null : Number(values.customerId),
    prospect_name: prospect ? values.prospectName.trim() : null,
    prospect_email: null,
    prospect_monthly_discount: prospect ? Number(values.discounts.monthly) : null,
    prospect_installment_discount: prospect ? Number(values.discounts.installment) : null,
    prospect_annual_discount: prospect ? Number(values.discounts.annual) : null,
    name: values.name.trim() || null,
    description: values.description.trim() || null,
    internal_note: null,
    // Issue 31: the API also trims + upper-cases; do it here too so the request matches what
    // will be stored.
    has_affiliate: values.hasAffiliate ?? false,
    affiliate_name: (values.affiliateName ?? "").trim().toUpperCase() || null,
    sections: values.sections.map(toSectionRequest),
  };
};
